import { X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, PrimaryButton } from '../ui';
import { cacheDueForSite, duesApi, getDuesSiteId } from './duesApi';
import { resolveDueDisplayLabels } from './duesDisplay';
import { formatCategoryLabel, getDueCancelReason } from './duesUtils';

const formatCurrency = (amount, currency = 'INR') => {
  try {
    return Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch {
    return `${currency} ${Number(amount || 0)}`;
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day} ${d.toLocaleString('en-US', { month: 'short' })} ${year} • ${hours}:${minutes}`;
  } catch {
    return String(value);
  }
};

const KeyValue = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 py-3 border-b border-gray-100 last:border-b-0">
    <div className="text-sm font-semibold text-gray-600">{label}</div>
    <div className="text-sm text-gray-900 break-words">{value ?? '—'}</div>
  </div>
);

const DueDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dueId } = useParams();

  const dueFromNavigation = useMemo(() => {
    const cached = location.state?.due;
    if (!cached) return null;
    const cachedId = cached.due_id || cached.dueId || cached.id;
    if (cachedId && String(cachedId) === String(dueId)) return cached;
    return null;
  }, [location.state, dueId]);

  const [due, setDue] = useState(dueFromNavigation);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');
  const [display, setDisplay] = useState(null);

  const badges = useMemo(() => {
    const status = due?.status || 'open';
    const paymentStatus =
      due?.payment_status ||
      due?.paymentStatus ||
      (status === 'paid' ? 'paid' : 'unpaid');
    const openStatus = status;
    return { paymentStatus, openStatus };
  }, [due]);

  const isCancelled = useMemo(() => {
    const status = String(due?.status || '').toLowerCase();
    return status === 'cancelled' || status === 'canceled';
  }, [due]);

  const cancellationReason = useMemo(
    () => getDueCancelReason(due),
    [due]
  );

  const load = async () => {
    if (!dueId) return;
    const hasCachedDue = !!dueFromNavigation;
    if (!hasCachedDue) setLoading(true);
    setError('');
    try {
      const res = await duesApi.getDue(dueId);
      setDue(res?.due || null);
    } catch (e) {
      if (!hasCachedDue) {
        setError(e?.message || 'Failed to load due.');
        setDue(null);
      } else {
        setError(
          e?.message ||
            'Could not refresh due details. Showing the last loaded copy.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dueFromNavigation) {
      setDue(dueFromNavigation);
    } else {
      setDue(null);
    }
    load();
  }, [dueId, dueFromNavigation]);

  useEffect(() => {
    let active = true;
    if (!due) {
      setDisplay(null);
      return undefined;
    }
    resolveDueDisplayLabels(due).then((labels) => {
      if (active) setDisplay(labels);
    });
    return () => {
      active = false;
    };
  }, [due]);

  const openCancelDialog = () => {
    setCancelReason('');
    setCancelReasonError('');
    setShowCancelDialog(true);
  };

  const closeCancelDialog = () => {
    if (cancelLoading) return;
    setShowCancelDialog(false);
    setCancelReason('');
    setCancelReasonError('');
  };

  const handleConfirmCancelDue = async (e) => {
    e.preventDefault();
    if (!dueId || !due || cancelLoading) return;

    const reason = cancelReason.trim();
    if (!reason) {
      setCancelReasonError('Please enter a reason for cancellation.');
      return;
    }

    setCancelLoading(true);
    setCancelReasonError('');
    setError('');
    try {
      const res = await duesApi.cancelDue(dueId, { reason });
      const siteId = due.site_id || due.siteId || getDuesSiteId();
      if (siteId) {
        const apiDue = res?.due || res || {};
        cacheDueForSite(siteId, {
          ...due,
          ...apiDue,
          due_id: apiDue.due_id || dueId,
          status: apiDue.status || 'cancelled',
          cancel_reason: getDueCancelReason(apiDue) || reason,
        });
      }
      setShowCancelDialog(false);
      navigate('/dues');
    } catch (err) {
      setCancelReasonError(err?.message || 'Failed to cancel due.');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen">
        <div className="mx-auto h-auto">
          <Card className="p-6">
            <p className="text-gray-700">Loading due...</p>
          </Card>
        </div>
      </div>
    );
  }

  if (!due) {
    return (
      <div className="w-full min-h-screen">
        <div className="mx-auto h-auto">
          <div className="rounded-3xl">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900">View Due</h1>
              <p className="mt-2 text-gray-600">{error || 'Due not found'}</p>
            </div>
            <Card className="p-6">
              <p className="text-gray-700">
                Please go back to the Dues list and select a due again.
              </p>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <div className="space-y-8">
          <div className="rounded-3xl">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-3xl font-bold text-gray-900">View Due</h1>
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                    {String(badges.openStatus || 'open').toUpperCase()}
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                    {String(badges.paymentStatus || 'unpaid').toUpperCase()}
                  </span>
                </div>

                <div className="mt-2 text-sm text-gray-700">
                  <div className="font-semibold text-gray-900">
                    {formatCurrency(due.amount, due.currency || 'INR')}
                  </div>
                  <div className="mt-1">
                    {due.title || formatCategoryLabel(due.category || due.type) || '—'}
                    {display?.unitLabel && display.unitLabel !== '—'
                      ? ` • ${display.unitLabel}`
                      : ''}
                  </div>
                  {display?.siteName && display.siteName !== '—' && (
                    <div className="mt-1 text-gray-600">{display.siteName}</div>
                  )}
                  <div className="mt-1 text-gray-600">
                    Created {formatDateTime(due.created_at || due.createdAt)}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <Card className="p-4 mb-6 border border-red-200 bg-red-50 text-red-800">
                {error}
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Due Information
                  </h3>
                  <div>
                    <KeyValue
                      label="Due reference"
                      value={display?.dueRef || '…'}
                    />
                    <KeyValue
                      label="Category"
                      value={formatCategoryLabel(due.category || due.type || 'other')}
                    />
                    <KeyValue label="Currency" value={due.currency || 'INR'} />
                    <KeyValue
                      label="Payment Status"
                      value={due.payment_status || due.paymentStatus || badges.paymentStatus}
                    />
                    <KeyValue
                      label="Resident"
                      value={
                        due.residentName ||
                        due.resident_name ||
                        due.resident ||
                        '—'
                      }
                    />
                    <KeyValue
                      label="Unit"
                      value={display?.unitLabel || '…'}
                    />
                    <KeyValue label="Site" value={display?.siteName || '…'} />
                    {isCancelled && (
                      <KeyValue
                        label="Cancellation reason"
                        value={cancellationReason || '—'}
                      />
                    )}
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h3>
                  <div className="overflow-x-auto rounded-2xl border border-gray-200">
                    <table className="min-w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-gray-600">
                          <th className="px-4 py-3 font-semibold min-w-[220px]">Item</th>
                          <th className="px-4 py-3 font-semibold min-w-[120px]">Qty</th>
                          <th className="px-4 py-3 font-semibold min-w-[140px]">Units</th>
                          <th className="px-4 py-3 font-semibold min-w-[180px] text-right">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(due.line_items || due.lineItems || []).length ? (
                          (due.line_items || due.lineItems).map((li) => (
                            <tr
                              key={
                                li.id ||
                                li.line_item_id ||
                                `${li.name || li.itemName}-${li.subtotal || li.amount}`
                              }
                              className="border-t border-gray-100"
                            >
                              <td className="px-4 py-3 text-gray-900">{li.name || li.itemName || '—'}</td>
                              <td className="px-4 py-3 text-gray-700">
                                {li.quantity ?? '—'}
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {li.unit || li.units || '—'}
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-gray-900">
                                {formatCurrency(
                                  li.subtotal ?? li.amount,
                                  due.currency || li.currency || 'INR'
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="border-t border-gray-100">
                            <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                              No line items.
                            </td>
                          </tr>
                        )}
                        <tr className="border-t border-gray-200 bg-gray-50 text-gray-900 font-semibold">
                          <td colSpan={3} className="px-4 py-3">
                            Total
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatCurrency(due.amount, due.currency || 'INR')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {due.description || '—'}
                  </p>
                </Card>
              </div>

              <div className="lg:col-span-1 space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity</h3>
                  <div>
                    <KeyValue label="Created" value={formatDateTime(due.created_at || due.createdAt)} />
                    <KeyValue label="Updated" value={formatDateTime(due.updated_at || due.updatedAt)} />
                  </div>
                </Card>

                {!isCancelled && (
                  <div className="flex flex-col gap-3">
                    <button
                      type="button"
                      className="w-full rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60 disabled:cursor-not-allowed"
                      onClick={openCancelDialog}
                      disabled={cancelLoading}
                    >
                      {cancelLoading ? 'Cancelling…' : 'Cancel'}
                    </button>

                    <PrimaryButton
                      type="button"
                      className="w-full min-h-[48px]"
                      onClick={() =>
                        navigate('/dues/create', { state: { due, mode: 'edit' } })
                      }
                    >
                      Edit Due
                    </PrimaryButton>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCancelDialog &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center min-h-screen w-full p-4 bg-black/50"
            onClick={closeCancelDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-due-dialog-title"
          >
            <Card
              variant="custom"
              className="max-w-md w-full p-5 bg-neutral-50/50 shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] border-white/20"
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={handleConfirmCancelDue}>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h2
                    id="cancel-due-dialog-title"
                    className="text-lg font-semibold text-gray-900"
                  >
                    Cancel due?
                  </h2>
                  <button
                    type="button"
                    onClick={closeCancelDialog}
                    disabled={cancelLoading}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-stone-950/10 text-gray-700 hover:bg-stone-950/20 disabled:opacity-50"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  This action cannot be undone. Please enter a reason below.
                </p>

                <label
                  htmlFor="cancel-due-reason"
                  className="mb-2 block text-sm font-semibold text-gray-700"
                >
                  Reason <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="cancel-due-reason"
                  value={cancelReason}
                  onChange={(e) => {
                    setCancelReason(e.target.value);
                    if (cancelReasonError) setCancelReasonError('');
                  }}
                  placeholder="Enter reason for cancellation"
                  rows={3}
                  disabled={cancelLoading}
                  required
                  className="w-full rounded-lg border border-white/40 bg-white/60 backdrop-blur-sm px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  autoFocus
                />
                {cancelReasonError && (
                  <p className="mt-2 text-sm font-semibold text-red-700">
                    {cancelReasonError}
                  </p>
                )}

                <div className="mt-5 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={closeCancelDialog}
                    disabled={cancelLoading}
                    className="h-10 px-4 rounded-lg border border-white/40 bg-white/40 backdrop-blur-sm text-gray-700 hover:bg-white/60 text-sm font-medium disabled:opacity-50"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={cancelLoading || !cancelReason.trim()}
                    className="h-10 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-semibold !text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cancelLoading ? 'Cancelling…' : 'Confirm Cancel'}
                  </button>
                </div>
              </form>
            </Card>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DueDetail;

